// AccountMenu.tsx — THE canonical Oriora account menu. IDENTICAL in every Oriora app.
//
// This file is the single source of truth: copy it in UNCHANGED. The ONLY per-app difference
// is the props passed at the call site (email, appSlug, appName, onSignedOut). Everything the
// account section needs lives HERE — never split it across App.tsx / iap.tsx again.
//
// Renders the avatar chip → popover:
//   • "Signed in as {email}"
//   • Sign out
//   • Wallet top-up FIRST: Apple IAP on iOS/MAS (<WalletTopUp/>) — Apple looks for this (2.1(b))
//   • Sign out · Account management… (a REAL button, not a grey link) → Manage account (web) ·
//     Delete account… (stays inside the sub-menu on purpose — destructive; two-step confirm)
//   • "How to use {appName} →" → orioralabs.com/apps/{appSlug}
//
// Depends only on the standard Oriora app primitives every clone already has:
//   ./supabase (shared auth), ./iap (WalletTopUp), the `open_external`/`open_delete_window`
//   Tauri commands, `platform()`, and the shared CSS tokens (--accent, --fs-*, .card/.btn/.muted).
import { useState, useRef, useEffect } from "react";
import { invoke } from "./tauri-web";
import { platform } from "./tauri-web";
import { supabase } from "./supabase";
import { WalletTopUp } from "./iap";

const ACCOUNT_URL = "https://orioralabs.com/settings";
const openExternal = (url: string) => void invoke("open_external", { url });

export const SUPPORT_EMAIL = "info@orioralabs.com";
/** Open the mail composer addressed to support, prefilled for triage. Native: the OS opener handles
 *  mailto: (Mail on iOS/macOS). Web: navigating to mailto: opens the browser's configured mail client. */
export function reportBug(appName: string, accountEmail?: string | null) {
  const subject = `[${appName}] Bug report`;
  const body = [
    "What happened:", "", "",
    "What I expected:", "", "",
    "Steps to reproduce:", "1.", "2.", "",
    "— details for our team —",
    `App: ${appName}`,
    `Platform: ${platform()}`,
    `Account: ${accountEmail || "(not signed in)"}`,
    `Screen: ${typeof window !== "undefined" ? `${window.innerWidth}×${window.innerHeight}` : "?"}`,
  ].join("\n");
  const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  if (platform() === "web") { window.location.href = url; return; }
  openExternal(url);
}

export function AccountMenu({
  email,
  appSlug,
  appName,
  onSignedOut,
}: {
  email: string | null;
  appSlug: string; // landing slug, e.g. "mood-tracker" → orioralabs.com/apps/<slug>
  appName: string; // display name, e.g. "Oriora Mood"
  onSignedOut?: () => void; // optional — apps with reactive onAuthStateChange don't need it
}) {
  const [open, setOpen] = useState(false);
  const [showMgmt, setShowMgmt] = useState(false);
  // 🔴 WHICH SIDE THE PANEL HANGS FROM IS NOT A CONSTANT (Cellar/Symptom Log 2026-08-28): mounted at the
  // LEFT of a settings row, a right-anchored 240px panel hangs off-screen and "Delete account" becomes
  // unreachable (Guideline 5.1.1(v)). Measure the avatar when the panel opens and pick the side that fits.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [side, setSide] = useState<"left" | "right">("right");
  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setSide(r.right - 240 < 8 ? "left" : "right");
  }, [open]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isIos = platform() === "ios";
  const isMasMac = platform() === "macos" && (import.meta as any).env?.VITE_MAS === "1";

  async function signOut() {
    try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
    onSignedOut?.();
  }
  async function deleteAccount() {
    const { data } = await supabase.auth.getSession();
    // Prefer the in-app delete window (hands the session in — no re-login). Apps whose Rust backend
    // doesn't register `open_delete_window` (older generation) throw → fall back to the shared web
    // delete page. Keeps this ONE component drop-in on every app with no Rust change.
    if (data.session) {
      try {
        await invoke("open_delete_window", { accessToken: data.session.access_token, refreshToken: data.session.refresh_token });
        return;
      } catch { /* command not registered on this app → web fallback below */ }
    }
    openExternal("https://orioralabs.com/account/delete");
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button aria-label="Account" onClick={() => setOpen((o) => !o)}
        style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--accent)", color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer" }}>
        {(email?.[0] ?? "?").toUpperCase()}
      </button>
      {open && (
        <>
          <div onClick={() => { setOpen(false); setShowMgmt(false); setConfirmDelete(false); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="card" style={{ position: "absolute", ...(side === "right" ? { right: 0 } : { left: 0 }), top: "calc(100% + 6px)", zIndex: 50, width: "min(240px, 88vw)", padding: "0.85rem", textAlign: "left", maxHeight: "75vh", overflowY: "auto" }}>
            <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 2 }}>Signed in as</div>
            <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, marginBottom: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>
            {/* 2026-09-02 — Apple rejected Chores twice (5.1.1(v)) and Reefline (2.1(b)): an iPad reviewer found
                neither "Delete account" nor "Add funds". Two things changed, ONE thing deliberately did not:
                  • Add funds is FIRST (it is what Apple looks for under 2.1(b)); it was below a scroll.
                  • "Account management…" is a real full-width button, not a muted grey text link.
                  • Delete account STAYS INSIDE Account management (Bogdan: "it's there for a reason") — a
                    destructive action is never placed beside Sign out. Two-step confirm kept. */}
            {(isIos || isMasMac) && (
              <div style={{ marginBottom: "0.85rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                <WalletTopUp />
              </div>
            )}
            <button className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", marginBottom: 6 }} onClick={signOut}>Sign out</button>
            {!showMgmt ? (
              <button onClick={() => setShowMgmt(true)}
                className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600 }}>Account management…</button>
            ) : !confirmDelete ? (
              <div style={{ marginTop: 6 }}>
                <button onClick={() => openExternal(ACCOUNT_URL)}
                  className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600, marginBottom: 8 }}>Manage your Oriora account →</button>
                <button onClick={() => setConfirmDelete(true)}
                  className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600, color: "#dc2626", borderColor: "#dc2626", marginBottom: 6 }}>Delete account…</button>
                <button onClick={() => setShowMgmt(false)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "var(--fs-xs)", color: "var(--muted, #8a8a8a)" }}>← back</button>
              </div>
            ) : (
              <div style={{ marginTop: 6 }}>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 8, lineHeight: 1.45 }}>This permanently deletes your <strong>entire Oriora account</strong> — across <strong>every Oriora app</strong>, not just this one. It can&apos;t be undone.</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ flex: 1, fontSize: "var(--fs-xs)", fontWeight: 600, background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "0.45rem", cursor: "pointer" }} onClick={deleteAccount}>Delete</button>
                  <button className="btn" style={{ flex: 1, fontSize: "var(--fs-xs)" }} onClick={() => { setConfirmDelete(false); setShowMgmt(false); }}>Cancel</button>
                </div>
              </div>
            )}
            <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)", display: "grid", gap: "0.4rem" }}>
              <button onClick={() => openExternal(`https://orioralabs.com/apps/${appSlug}`)}
                className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600 }}>
                How to use {appName} →
              </button>
              {/* Bug reports go to a human by email (Bogdan 2026-09-04: "give users a way to report bugs — in app,
                  connected to our email"). The in-app chat is the app's assistant, not support, so this is the
                  ONE explicit path: it opens the device's mail composer addressed to us, prefilled with what we
                  need for triage. Nothing is sent until the customer taps Send in their mail app. */}
              <button onClick={() => reportBug(appName, email)}
                className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600 }}>
                Report a bug ✉
              </button>
              <div className="muted" style={{ fontSize: "10px", textAlign: "center" }}>
                Or email {SUPPORT_EMAIL}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
